import React, { useState, useEffect } from 'react';
import axios from 'axios';

const API = '/api/master-data';

const Icon = ({ d, size = 16, strokeWidth = 1.75 }) => (
    <svg
        width={size} height={size} viewBox="0 0 24 24"
        fill="none" stroke="currentColor"
        strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round"
    >
        {Array.isArray(d) ? d.map((p, i) => <path key={i} d={p} />) : <path d={d} />}
    </svg>
);

const icons = {
    route: ['M9 18l6-6-6-6'],
    status: ['M22 11.08V12a10 10 0 1 1-5.93-9.14', 'M22 4L12 14.01l-3-3'],
    plus: ['M12 5v14', 'M5 12h14'],
    search: ['M11 19a8 8 0 1 0 0-16 8 8 0 0 0 0 16z', 'M21 21l-4.35-4.35'],
    edit: ['M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7', 'M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z'],
    trash: ['M3 6h18', 'M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2'],
    x: ['M18 6 6 18', 'M6 6l12 12'],
    alertTriangle: ['M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z', 'M12 9v4', 'M12 17h.01'],
    mapPin: ['M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z', 'M12 7a3 3 0 1 0 0 6 3 3 0 0 0 0-6z'],
};

const COLOR_PALETTE = [
    '#4F6EF7', '#22C55E', '#EF4444', '#F59E0B', '#A855F7',
    '#EC4899', '#6366F1', '#14B8A6', '#F97316', '#6B7280',
    '#1E293B', '#06B6D4', '#84CC16', '#DC2626', '#9333EA',
    '#3B82F6',
];

const TABS = [
    { key: 'route-zone', label: 'Zones', iconKey: 'mapPin', description: 'Define operational areas and coverage distances.' },
    
];

function ItemModal({ tab, initial, onClose, onSave }) {
    const isEdit = !!initial;
    const isZone = tab.key === 'route-zone';
    const [form, setForm] = useState({ name: '', description: '', distance: '', color: '#4F6EF7', active: true, ...(initial || {}) });
    const [saving, setSaving] = useState(false);

    const handleSave = async () => {
        if (!form.name.trim()) return;
        setSaving(true);
        try {
            await onSave({ ...form, distance: parseFloat(form.distance) || 0 });
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="md-modal-overlay" onClick={onClose}>
            <div className={`md-modal ${isZone ? 'zone-modal' : ''}`} onClick={e => e.stopPropagation()}>
                <div className="md-modal-header" style={isZone ? { background: '#f8fafc', borderBottom: '1px solid #e2e8f0', padding: '20px 24px' } : {}}>
                    <div>
                        <h3 className="md-modal-title" style={{ fontSize: 18, fontWeight: 700 }}>{isEdit ? `Edit ${tab.label.slice(0, -1)}` : `Add New ${tab.label.slice(0, -1)}`}</h3>
                        <p style={{ fontSize: 12, color: '#64748b', marginTop: 2 }}>{isZone ? 'Configure operational zone details and coverage.' : `Manage ${tab.label.toLowerCase()} details.`}</p>
                    </div>
                    <button className="md-modal-close" onClick={onClose}><Icon d={icons.x} size={18} /></button>
                </div>

                <div className="md-modal-body" style={{ padding: '24px' }}>
                    <div className={isZone ? "zone-form-grid" : ""}>
                        <div className="md-form-group">
                            <label className="md-label">Zone Name<span className="md-required">*</span></label>
                            <input
                                className="md-input border-premium"
                                value={form.name}
                                onChange={e => setForm({ ...form, name: e.target.value })}
                                placeholder={isZone ? "e.g. Northern Region, Downtown" : `Enter ${tab.label.toLowerCase()} name`}
                            />
                        </div>

                        {isZone && (
                            <div className="md-form-group">
                                <label className="md-label">Operating Distance (km)</label>
                                <div style={{ position: 'relative' }}>
                                    <input
                                        type="number"
                                        className="md-input border-premium"
                                        value={form.distance}
                                        onChange={e => setForm({ ...form, distance: e.target.value })}
                                        placeholder="0"
                                        style={{ paddingRight: 45 }}
                                    />
                                    <span style={{ position: 'absolute', right: 14, top: '50%', transform: 'translateY(-50%)', fontSize: 12, fontWeight: 600, color: '#94a3b8' }}>KM</span>
                                </div>
                            </div>
                        )}
                    </div>

                    <div className="md-form-group" style={{ marginTop: 16 }}>
                        <label className="md-label">Detailed Description</label>
                        <textarea
                            className="md-textarea border-premium"
                            rows={3}
                            value={form.description}
                            onChange={e => setForm({ ...form, description: e.target.value })}
                            placeholder="Provide additional context or operational notes..."
                        />
                    </div>

                    {!isZone && (
                        <div className="md-form-group" style={{ marginTop: 16 }}>
                            <label className="md-label">Identify Color</label>
                            <div className="md-color-palette">
                                {COLOR_PALETTE.map(c => (
                                    <button
                                        key={c}
                                        className={`md-color-swatch ${form.color === c ? 'md-color-active' : ''}`}
                                        style={{ background: c }}
                                        onClick={() => setForm({ ...form, color: c })}
                                    />
                                ))}
                            </div>
                        </div>
                    )}

                    <div className="md-form-toggle-row premium-toggle-box">
                        <div style={{ display: 'flex', flexDirection: 'column' }}>
                            <label className="md-toggle-label" style={{ marginBottom: 2, fontWeight: 600 }}>Active Status</label>
                            <span style={{ fontSize: 12, color: '#64748b' }}>{form.active ? 'Available for selection in routes.' : 'Currently disabled in the system.'}</span>
                        </div>
                        <button className={`md-toggle ${form.active ? 'md-toggle-on' : ''}`} onClick={() => setForm({ ...form, active: !form.active })}>
                            <span className="md-toggle-thumb" />
                        </button>
                    </div>
                </div>

                <div className="md-modal-footer" style={{ borderTop: '1px solid #e2e8f0', padding: '16px 24px' }}>
                    <button className="md-cancel-btn" onClick={onClose} style={{ borderRadius: 10, fontWeight: 600 }}>Discard</button>
                    <button
                        className="md-save-btn btn-premium"
                        onClick={handleSave}
                        disabled={saving || !form.name.trim()}
                        style={isZone ? { background: '#1e293b' } : {}}
                    >
                        {saving ? 'Processing...' : (isEdit ? 'Save Changes' : 'Create Zone')}
                    </button>
                </div>
            </div>
        </div>
    );
}


function DeleteModal({ itemName, onClose, onConfirm }) {
    const [deleting, setDeleting] = useState(false);
    return (
        <div className="md-modal-overlay" onClick={onClose}>
            <div className="md-modal md-modal-sm" style={{ width: 400 }} onClick={e => e.stopPropagation()}>
                <div className="md-modal-header">
                    <h3 className="md-modal-title" style={{ color: '#dc2626' }}>
                        <Icon d={icons.alertTriangle} size={18} /> Confirm Delete
                    </h3>
                    <button className="md-modal-close" onClick={onClose}><Icon d={icons.x} size={18} /></button>
                </div>
                <div className="md-modal-body">
                    <p style={{ color: '#475569', lineHeight: 1.6, margin: 0 }}>
                        Are you sure you want to delete <strong>"{itemName}"</strong>? This action cannot be undone.
                    </p>
                </div>
                <div className="md-modal-footer">
                    <button className="md-cancel-btn" onClick={onClose}>Cancel</button>
                    <button
                        className="md-save-btn"
                        style={{ background: '#dc2626' }}
                        onClick={async () => {
                            setDeleting(true);
                            await onConfirm();
                            setDeleting(false);
                        }}
                        disabled={deleting}
                    >
                        {deleting ? 'Deleting...' : 'Delete'}
                    </button>
                </div>
            </div>
        </div>
    );
}

export default function RouteSettings() {
    const [activeTab, setActiveTab] = useState(TABS[0].key);
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(false);
    const [search, setSearch] = useState('');
    const [modal, setModal] = useState(null);
    const [deleteModal, setDeleteModal] = useState(null);

    useEffect(() => {
        fetchItems();
    }, [activeTab]);

    const fetchItems = async () => {
        setLoading(true);
        try {
            const res = await axios.get(API);
            setItems(res.data.filter(d => d.category === activeTab));
        } catch (err) { console.error(err); }
        finally { setLoading(false); }
    };

    const handleSave = async (form) => {
        const payload = { ...form, category: activeTab };
        if (form._id) {
            await axios.put(`${API}/${form._id}`, payload);
        } else {
            await axios.post(API, payload);
        }
        setModal(null);
        fetchItems();
    };

    const handleDelete = async () => {
        if (deleteModal?._id) {
            await axios.delete(`${API}/${deleteModal._id}`);
        }
        setDeleteModal(null);
        fetchItems();
    };

    const filtered = items.filter(i =>
        i.name.toLowerCase().includes(search.toLowerCase()) ||
        (i.description || '').toLowerCase().includes(search.toLowerCase())
    );

    const currentTabObj = TABS.find(t => t.key === activeTab);

    return (
        <div className="ts-container">
            {/* Sidebar like Ticket Settings */}
            <aside className="ts-sidebar">
                <div className="ts-nav-item active" style={{ marginBottom: 24, cursor: 'default', background: 'none', color: '#1e293b', paddingLeft: 8 }}>
                    <span style={{ fontSize: 13, fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase', opacity: 0.5 }}>Route Configuration</span>
                </div>
                {TABS.map(tab => (
                    <div
                        key={tab.key}
                        className={`ts-nav-item ${activeTab === tab.key ? 'active' : ''}`}
                        onClick={() => setActiveTab(tab.key)}
                    >
                        <Icon d={icons[tab.iconKey]} size={18} />
                        <span>{tab.label}</span>
                    </div>
                ))}
            </aside>

            {/* Main Content Area */}
            <main className="ts-main">
                <header className="ts-header">
                    <div className="ts-title-group">
                        <h1>{currentTabObj.label}</h1>
                        <p>{currentTabObj.description}</p>
                    </div>
                    <div className="ts-controls">
                        <div className="md-search-wrap" style={{ margin: 0 }}>
                            <Icon d={icons.search} size={14} />
                            <input className="md-search" placeholder="Quick search..." value={search} onChange={e => setSearch(e.target.value)} />
                        </div>
                        <button className="md-add-btn" onClick={() => setModal('add')}>
                            <Icon d={icons.plus} size={14} /> Add {currentTabObj.label.slice(0, -1)}
                        </button>
                    </div>
                </header>

                <div className="md-panel" style={{ background: '#fff', borderRadius: 16, border: '1px solid #e2e8f0', overflow: 'hidden' }}>
                    <div className="md-table-wrap">
                        <table className="md-table">
                            <thead>
                                <tr>
                                    <th className="md-th">Name</th>
                                    <th className="md-th">Description</th>
                                    {activeTab === 'route-zone' && <th className="md-th">Coverage</th>}
                                    <th className="md-th">Status</th>
                                    <th className="md-th">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {loading ? (
                                    <tr><td colSpan={activeTab === 'route-zone' ? 5 : 4} className="md-td" style={{ textAlign: 'center', padding: 40 }}>Loading...</td></tr>
                                ) : filtered.length === 0 ? (
                                    <tr>
                                        <td colSpan={activeTab === 'route-zone' ? 5 : 4} className="md-td" style={{ textAlign: 'center', padding: 60 }}>
                                            <Icon d={icons[currentTabObj.iconKey]} size={48} strokeWidth={1} style={{ opacity: 0.2, marginBottom: 12 }} />
                                            <p style={{ color: '#94a3b8' }}>No {currentTabObj.label} found.</p>
                                        </td>
                                    </tr>
                                ) : (
                                    filtered.map(item => (
                                        <tr key={item._id} className="md-tr">
                                            <td className="md-td">
                                                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                                                    {activeTab !== 'route-zone' && (
                                                        <span className="md-color-dot" style={{ background: item.color, width: 10, height: 10, borderRadius: '50%' }} />
                                                    )}
                                                    <strong style={{ color: '#1e293b' }}>{item.name}</strong>
                                                </div>
                                            </td>
                                            <td className="md-td" style={{ color: '#64748b', fontSize: 13, maxWidth: 300 }}>
                                                {item.description || '—'}
                                            </td>
                                            {activeTab === 'route-zone' && (
                                                <td className="md-td">
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontWeight: 600, color: '#4f46e5' }}>
                                                        <Icon d={icons.mapPin} size={14} />
                                                        {item.distance || 0} km
                                                    </div>
                                                </td>
                                            )}
                                            <td className="md-td">
                                                <span className={`md-status-badge ${item.active ? 'md-badge-active' : 'md-badge-inactive'}`}>
                                                    {item.active ? 'Active' : 'Inactive'}
                                                </span>
                                            </td>
                                            <td className="md-td md-actions">
                                                <button className="md-icon-btn" onClick={() => setModal(item)} title="Edit"><Icon d={icons.edit} size={14} /></button>
                                                <button className="md-icon-btn md-icon-btn-danger" onClick={() => setDeleteModal(item)} title="Delete"><Icon d={icons.trash} size={14} /></button>
                                            </td>
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                </div>
            </main>

            {modal && <ItemModal tab={currentTabObj} initial={modal === 'add' ? null : modal} onClose={() => setModal(null)} onSave={handleSave} />}
            {deleteModal && <DeleteModal itemName={deleteModal.name} onClose={() => setDeleteModal(null)} onConfirm={handleDelete} />}
        </div>
    );
}
